import { signedRequestWithRetry } from './http.js';
import { toUrlSafeNoPad } from './base64.js';
import {
  NotExist,
  TooLarge,
  Unavailable,
  WireError,
  WireVersionMismatch,
  throwForStatus,
} from './errors.js';
import {
  ACCESS_LEVEL_TO_INT,
  WIRE_VERSION,
  type AccessLevel,
  type AuthProvider,
  type DeleteResult,
  type EventBatch,
  type RetryPolicy,
  type ShareResult,
  type WireEvent,
  type WriteResult,
  DEFAULT_RETRY,
} from './types.js';

export interface AggregateClientDeps {
  baseUrl: string;
  fetchFn: typeof globalThis.fetch;
  auth: AuthProvider;
  retry: RetryPolicy;
  now: () => number;
  // Monotonic per-Gateway nonce source (W1): never returns the same value twice, so an
  // app-start burst from one keypair can't collide into a 401 replay.
  nextNonce: () => string;
  sleep: (ms: number) => Promise<void>;
}

export interface ReadOptions {
  fromVersion?: number;
  shareIdHash?: string;
  ownEvents?: boolean;
  signal?: AbortSignal;
}

export interface WriteOptions {
  createIfNotExist?: boolean;
  expectedAggregateVersion?: number;
}

export interface ShareOptions {
  accessLevel: AccessLevel;
  singleUse: boolean;
  expiresOn?: number;
}

interface ReadResponseJson {
  event_batches: ReadBatchJson[];
  next_aggregate_version: number | undefined;
}

interface ReadBatchJson {
  aggregate_version: number;
  server_timestamp: number;
  client_id: string;
  user_id?: string | null;
  events: WireEvent[];
}

interface WriteResponseJson {
  aggregate_version: number;
  write_backoff_ms?: number;
}

export class AggregateClient {
  constructor(
    private readonly deps: AggregateClientDeps,
    readonly type: string,
    readonly id: string,
  ) {
    if (id.length === 0) throw new Error('aggregate id must not be empty');
    if (type.length === 0) throw new Error('aggregate type must not be empty');
  }

  async read(options: ReadOptions = {}): Promise<EventBatch[]> {
    const fromVersion = options.fromVersion ?? 0;
    const all: EventBatch[] = [];
    let cursor = fromVersion;
    while (true) {
      if (options.signal?.aborted === true) throw makeAbortError();
      const params = new URLSearchParams();
      params.set('from_aggregate_version', String(cursor));
      if (options.shareIdHash !== undefined) params.set('share_id', toUrlSafeNoPad(options.shareIdHash));
      if (options.ownEvents === true) params.set('own_events', 'true');

      const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/read?${params.toString()}`;
      const response = await this.requestWithRetry(url, { method: 'GET' });
      // The gateway 404s an ABSENT stream — which is both "never written" (benign) and
      // "deleted" (the caller must know: a deleted-elsewhere aggregate purges local
      // state, W2/D5). The one benign case is a first-ever read: page 1, from the
      // stream's start, nothing accumulated — surface that as empty. Every other 404 —
      // a cursor past the start (the caller HAD server state) or a later pagination page
      // (stream deleted mid-read; returning the partial list would present truncation as
      // a complete read) — throws NotExist.
      if (response.status === 404) {
        if (cursor === fromVersion && fromVersion <= 1 && all.length === 0) return all;
        throw new NotExist(url, response.headers.get('x-wire-version') ?? undefined);
      }
      await throwForStatus(response, url);
      assertWireVersion(response, url);
      const body = await parseJson<ReadResponseJson>(response, url);
      for (const b of body.event_batches) {
        const batch: EventBatch = {
          aggregateVersion: b.aggregate_version,
          serverTimestamp: b.server_timestamp,
          clientId: b.client_id,
          events: b.events,
        };
        if (b.user_id !== null && b.user_id !== undefined) batch.userId = b.user_id;
        all.push(batch);
      }
      if (body.next_aggregate_version === undefined || body.next_aggregate_version === null) break;
      cursor = body.next_aggregate_version;
    }
    return all;
  }

  // Claims a share code: ONE GET from version 0 carrying the share_id, which is what turns the
  // code into a durable grant for this principal. The response body is discarded — the caller
  // wants the grant, not the history — so this deliberately does NOT paginate the way read()
  // does, and a single-use code is presented exactly once.
  // read()'s benign-404 rule (a first-ever read of a never-written stream) must NOT apply here:
  // on redeem a 404 means the stream is absent, so the code granted nothing usable. It falls
  // through to throwForStatus as NotExist, like every other non-2xx.
  async redeemShare(shareIdHash: string): Promise<void> {
    const params = new URLSearchParams();
    params.set('from_aggregate_version', '0');
    params.set('share_id', toUrlSafeNoPad(shareIdHash));
    const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/read?${params.toString()}`;
    const response = await this.requestWithRetry(url, { method: 'GET' });
    await throwForStatus(response, url);
    assertWireVersion(response, url);
  }

  // Returns the aggregate_version this write's events landed at. Under interleaving with
  // other writers, this is YOUR events' version, not the global current version; treat the
  // server's later reads as the source of truth for catching up.
  async write(events: WireEvent[], options: WriteOptions = {}): Promise<WriteResult> {
    if (events.length === 0) throw new Error('write requires at least one event');
    const params = new URLSearchParams();
    if (options.createIfNotExist === true) params.set('create_if_not_exist', 'true');
    if (options.expectedAggregateVersion !== undefined) {
      params.set('expected_aggregate_version', String(options.expectedAggregateVersion));
    }
    const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/write?${params.toString()}`;

    const occInPlay = options.expectedAggregateVersion !== undefined;
    const results = await this.writeWithSplit(url, events, occInPlay);
    const last = results[results.length - 1]!;
    const out: WriteResult = { aggregateVersion: last.aggregateVersion };
    // The version comes from the LAST sub-batch (that is where the caller's events ended up), but
    // the backoff hint is the last DEFINED one anywhere in the split. Each sub-batch is its own
    // write on the wire, so a hint on an earlier one is a real ask the gateway made; taking
    // `last.writeBackoffMs` alone dropped it whenever the final sub-batch answered unhinted.
    for (let i = results.length - 1; i >= 0; i -= 1) {
      const hint = results[i]!.writeBackoffMs;
      if (hint !== undefined) { out.writeBackoffMs = hint; break; }
    }
    return out;
  }

  // Splits on 413 unless OCC is in play: splitting an OCC write is mathematically broken
  // (only the first sub-batch can satisfy expected_aggregate_version=N; the second sub-batch
  // would have to use N+sub1, which we can't predict mid-flight). Under OCC, a 413 is a hard
  // TooLarge for the caller to handle (split the event batch upstream before committing).
  //
  // ACCEPTED COST: the sub-batch writes go back to back, so a hint returned mid-split paces the
  // caller's NEXT write but not the rest of this one. Pacing here would mean duplicating the
  // SDK's MAX_WRITE_BACKOFF_MS ceiling in the wire package (this layer holds no pacing state and
  // must not obey an unbounded hint), and a 413 on an oversized batch is a rare path.
  private async writeWithSplit(url: string, events: WireEvent[], occInPlay: boolean): Promise<WriteResult[]> {
    const single = await this.attemptWrite(url, events);
    if (single !== undefined) return [single];

    if (occInPlay) throw new TooLarge(url, undefined);
    if (events.length === 1) throw new TooLarge(url, undefined);

    const mid = Math.floor(events.length / 2);
    const first = await this.writeWithSplit(url, events.slice(0, mid), false);
    const second = await this.writeWithSplit(url, events.slice(mid), false);
    return [...first, ...second];
  }

  private async attemptWrite(url: string, events: WireEvent[]): Promise<WriteResult | undefined> {
    const response = await this.requestWithRetry(url, {
      method: 'POST',
      body: JSON.stringify(events),
    });
    if (response.status === 413) return undefined;
    await throwForStatus(response, url);
    assertWireVersion(response, url);
    const body = await parseJson<WriteResponseJson>(response, url);
    const result: WriteResult = { aggregateVersion: body.aggregate_version };
    if (body.write_backoff_ms !== undefined) result.writeBackoffMs = body.write_backoff_ms;
    return result;
  }

  async share(options: ShareOptions): Promise<ShareResult> {
    const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/share`;
    const body = JSON.stringify({
      access_level: ACCESS_LEVEL_TO_INT[options.accessLevel],
      is_single_use: options.singleUse,
      expires_on: options.expiresOn ?? 0,
    });
    const response = await this.requestWithRetry(url, { method: 'POST', body });
    await throwForStatus(response, url);
    assertWireVersion(response, url);
    const parsed = await parseJson<{ share_key: string }>(response, url);
    return { shareKey: parsed.share_key };
  }

  async disableShare(shareHash: string): Promise<void> {
    await this.postEmpty(`disableshare/${toUrlSafeNoPad(shareHash)}`);
  }

  async disableUser(userHash: string): Promise<void> {
    await this.postEmpty(`disableuser/${toUrlSafeNoPad(userHash)}`);
  }

  async disableClient(clientHash: string): Promise<void> {
    await this.postEmpty(`disableclient/${toUrlSafeNoPad(clientHash)}`);
  }

  async delete(): Promise<DeleteResult> {
    const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/delete`;
    const response = await this.requestWithRetry(url, { method: 'POST' });
    await throwForStatus(response, url);
    assertWireVersion(response, url);
    const parsed = await parseJson<{ server_timestamp: number }>(response, url);
    return { serverTimestamp: parsed.server_timestamp };
  }

  // Removes the caller's OWN access to this aggregate (the gateway drops it from the caller's
  // index). The owner can't leave (delete instead); the gateway 403s that.
  async leave(): Promise<void> {
    await this.postEmpty('leave');
  }

  private async postEmpty(action: string): Promise<void> {
    const url = `${this.deps.baseUrl}/api/v1/${encodeURIComponent(this.type)}/${encodeURIComponent(this.id)}/${action}`;
    const response = await this.requestWithRetry(url, { method: 'POST' });
    await throwForStatus(response, url);
    assertWireVersion(response, url);
  }

  private requestWithRetry(url: string, init: RequestInit): Promise<Response> {
    return signedRequestWithRetry(this.deps, url, init);
  }
}

function assertWireVersion(response: Response, url: string): void {
  const got = response.headers.get('X-Wire-Version');
  if (got === null) return;
  if (got !== WIRE_VERSION) throw new WireVersionMismatch(url, WIRE_VERSION, got);
}

async function parseJson<T>(response: Response, url: string): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch (e: unknown) {
    throw new WireError(`malformed JSON response: ${(e as Error).message}`, response.status, url, response.headers.get('X-Wire-Version') ?? undefined);
  }
}

function makeAbortError(): Error {
  const e = new Error('Aborted');
  e.name = 'AbortError';
  return e;
}

export { DEFAULT_RETRY, Unavailable };
